import { useState } from "react";
import { Alert, Button, Card, Form, Input, Space, Typography } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { api, ApiError } from "../api/client";
import { palette } from "../theme";
import { BrandBlock, LangSwitch } from "./LoginPage";
import { useIsMobile } from "../components/useIsMobile";
import { PasswordField, usePasswordRules } from "../components/PasswordField";

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const mobile = useIsMobile();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pwdRules = usePasswordRules();

  const submit = async (v: { name?: string; password: string }) => {
    setBusy(true);
    setErr(null);
    try {
      await api.post("/auth/accept-invite", { token, password: v.password, ...(v.name?.trim() ? { name: v.name.trim() } : {}) });
      await refresh();
      nav("/", { replace: true });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("a_loginFail"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: palette.bg, padding: mobile ? 12 : 16 }}>
      <Card style={{ width: "100%", maxWidth: 460 }} styles={{ body: { padding: mobile ? 20 : 28 } }}>
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <BrandBlock />
            <LangSwitch />
          </div>
          <div>
            <Typography.Title level={3} style={{ margin: 0, color: palette.heading }}>
              {t("a_inviteTitle")}
            </Typography.Title>
            <Typography.Text type="secondary">{t("a_inviteSub")}</Typography.Text>
          </div>
          {err && <Alert type="error" showIcon message={err} />}
          <Form layout="vertical" onFinish={submit} requiredMark={false} disabled={busy}>
            <Form.Item name="name" label={t("fullName")}>
              <Input autoComplete="name" size="large" style={{ fontSize: 16 }} />
            </Form.Item>
            <Form.Item name="password" label={t("a_password")} rules={pwdRules}>
              <PasswordField />
            </Form.Item>
            <Form.Item
              name="confirm"
              label={t("a_passwordConfirm")}
              dependencies={["password"]}
              hasFeedback
              rules={[
                { required: true, message: t("required") },
                ({ getFieldValue }) => ({
                  validator: (_, val) => (!val || getFieldValue("password") === val ? Promise.resolve() : Promise.reject(new Error(t("a_pwdMismatch")))),
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" size="large" style={{ fontSize: 16 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={busy} style={{ height: 48 }}>
              {t("a_inviteGo")}
            </Button>
          </Form>
        </Space>
      </Card>
    </div>
  );
}
